import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Globe, 
  Plus, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Trash2,
  RefreshCw,
  AlertCircle,
  Settings,
  Zap,
  Shield,
  TrendingUp,
  Users,
  Database,
  Server,
  X,
  Check,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import toast from 'react-hot-toast';
import domainApi from '../../services/seller/domainApi';

const DomainManagement = () => {
  const { user } = useAuth();
  const [mode, setMode] = useState('gmc');
  const [domainInput, setDomainInput] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainList, setDomainList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkAttempt, setCheckAttempt] = useState(0);
  const [maxAttempts] = useState(20);
  const [activeTab, setActiveTab] = useState('add');
  const [suggestions, setSuggestions] = useState([]);
  const [isValidating, setIsValidating] = useState(false);

  // Lấy danh sách domain từ database
  const fetchDomains = async () => {
    try {
      if (!user?.id) {
        console.error('User ID không tồn tại');
        return;
      }
      
      const response = await domainApi.getDomainsByUser(user.id);
      if (response.data.success) {
        setDomainList(response.data.domains || []);
      }
    } catch (error) {
      console.error('Lỗi khi lấy danh sách domain:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchDomains();
    }
  }, [user]);

  // Validate domain format
  const validateDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };

  // Check if domain is duplicate
  const isDuplicate = (domain) => {
    return domains.some(d => d.name.toLowerCase() === domain.toLowerCase()) ||
           domainList.some(d => d.domain_name.toLowerCase() === domain.toLowerCase());
  };

  // Add domain to list
  const addDomain = (domain) => {
    const cleanDomain = domain.trim().toLowerCase();
    
    if (!cleanDomain) return;

    if (!validateDomain(cleanDomain)) {
      toast.error(`Domain "${cleanDomain}" không hợp lệ!`);
      return;
    }

    if (isDuplicate(cleanDomain)) {
      toast.error(`Domain "${cleanDomain}" đã tồn tại!`);
      return;
    }

    const newDomain = {
      id: Date.now(),
      name: cleanDomain,
      status: 'pending',
      isValid: true,
      isDuplicate: false
    };

    setDomains([...domains, newDomain]);
    setDomainInput('');
    setSuggestions([]);
  };

  // Remove domain from list
  const removeDomain = (domainId) => {
    setDomains(domains.filter(d => d.id !== domainId));
  };

  // Handle input change with suggestions
  const handleInputChange = (value) => {
    setDomainInput(value);
    
    if (value.trim()) {
      // Generate suggestions based on input
      const suggestions = [
        `${value}.com`,
        `${value}.net`,
        `${value}.org`,
        `${value}.io`,
        `${value}.co`,
        `${value}.app`
      ].filter(suggestion => !isDuplicate(suggestion));
      
      setSuggestions(suggestions.slice(0, 4));
    } else {
      setSuggestions([]);
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDomain(domainInput);
    } else if (e.key === ',' || e.key === ';') {
      e.preventDefault();
      addDomain(domainInput);
    }
  };

  // Bulk add domains from text
  const handleBulkAdd = (text) => {
    const domainArray = text
      .split(/[\n,;\s]+/)
      .map(d => d.trim())
      .filter(d => d.length > 0);

    let addedCount = 0;
    let errorCount = 0;

    domainArray.forEach(domain => {
      if (validateDomain(domain) && !isDuplicate(domain)) {
        const newDomain = {
          id: Date.now() + Math.random(),
          name: domain.toLowerCase(),
          status: 'pending',
          isValid: true,
          isDuplicate: false
        };
        setDomains(prev => [...prev, newDomain]);
        addedCount++;
      } else {
        errorCount++;
      }
    });

    if (addedCount > 0) {
      toast.success(`Đã thêm ${addedCount} domain thành công!`);
    }
    if (errorCount > 0) {
      toast.error(`${errorCount} domain không hợp lệ hoặc trùng lặp!`);
    }
  };

  // Xử lý submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (domains.length === 0) {
      toast.error('Vui lòng thêm ít nhất một domain!');
      return;
    }

    setLoading(true);

    try {
      // Gửi domain ra hệ thống bên ngoài
      let endpoint = '';
      if (mode === 'gmc') {
        endpoint = 'https://linen-muse.com/ghi-domain.php';
      } else if (mode === 'run') {
        endpoint = 'https://linen-muse.com/ghi-domain1.php';
      } else if (mode === 'sample') {
        endpoint = 'https://linen-muse.com/ghi-domain2.php';
      }

      const domainsText = domains.map(d => d.name).join('\n');
      const externalResponse = await domainApi.sendToExternalSystem(endpoint, domainsText);
      
      if (!externalResponse.ok) {
        throw new Error('Lỗi khi gửi domain ra hệ thống bên ngoài');
      }

      const externalData = await externalResponse.json();
      toast.success(externalData.message || 'Đã gửi domain thành công!');

      // Ghi domain vào database nội bộ
      const internalResponse = await domainApi.addDomain({
        domains: domainsText,
        mode,
        userId: user.id
      });

      if (internalResponse.data.success) {
        toast.success(internalResponse.data.message);
        
        // Refresh danh sách domain
        await fetchDomains();
        
        // Clear current domains
        setDomains([]);

        // Bắt đầu kiểm tra domain
        const domainArray = domains.map(d => d.name);
        if (domainArray.length > 0) {
          startDomainCheck(domainArray);
        }
      } else {
        throw new Error('Lỗi khi lưu domain vào database');
      }

    } catch (error) {
      console.error('Lỗi:', error);
      toast.error('Có lỗi xảy ra khi xử lý domain!');
    } finally {
      setLoading(false);
    }
  };

  // Kiểm tra trạng thái domain
  const startDomainCheck = (domainArray) => {
    setChecking(true);
    setCheckAttempt(0);

    const checkInterval = setInterval(() => {
      setCheckAttempt(prev => {
        const currentAttempt = prev + 1;
        
        if (currentAttempt >= maxAttempts) {
          clearInterval(checkInterval);
          setChecking(false);
          toast.success('Hoàn tất kiểm tra domain!');
          return currentAttempt;
        }

        // Kiểm tra từng domain
        domainArray.forEach(domain => {
          checkSingleDomain(domain);
        });

        return currentAttempt;
      });
    }, 30000); // Kiểm tra mỗi 30 giây
  };

  const checkSingleDomain = async (domain) => {
    try {
      const response = await fetch(`https://${domain}`, { 
        method: 'HEAD', 
        mode: 'no-cors' 
      });
      
      // Tìm domain trong danh sách để lấy ID
      const domainRecord = domainList.find(d => d.domain_name === domain);
      if (domainRecord) {
        // Cập nhật trạng thái trong database
        await domainApi.updateDomainStatus(domainRecord.id, 'active');
        
        // Refresh danh sách
        fetchDomains();
      }
      
    } catch (error) {
      console.log(`Domain ${domain} chưa hoạt động`);
    }
  };

  // Xóa domain
  const deleteDomain = async (domainId) => {
    if (!confirm('Bạn có chắc muốn xóa domain này?')) return;

    try {
      const response = await domainApi.deleteDomain(domainId);
      if (response.data.success) {
        toast.success('Xóa domain thành công!');
        fetchDomains();
      } else {
        toast.error('Lỗi khi xóa domain!');
      }
    } catch (error) {
      console.error('Lỗi khi xóa domain:', error);
      toast.error('Lỗi khi xóa domain!');
    }
  };

  // Lấy icon cho trạng thái
  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-500" />;
    }
  };

  // Lấy text cho trạng thái
  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Hoạt động';
      case 'pending':
        return 'Đang xử lý';
      case 'error':
        return 'Lỗi';
      default:
        return 'Không xác định';
    }
  };

  // Thống kê
  const stats = {
    total: domainList.length,
    active: domainList.filter(d => d.status === 'active').length,
    pending: domainList.filter(d => d.status === 'pending').length,
    error: domainList.filter(d => d.status === 'error').length
  };

     return (
     <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
       <div className="p-6 lg:p-8">
         {/* Header với hiệu ứng 3D */}
         <div className="bg-gradient-to-br from-white/10 via-blue-500/20 to-purple-600/20 backdrop-blur-xl rounded-3xl shadow-2xl mb-8 p-8 border border-white/20">
                                 <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-0 mb-6">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mr-4">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white">Website Builder</h1>
                  <p className="text-gray-300">Tạo và quản lý các domain website của bạn với công nghệ tiên tiến</p>
                </div>
              </div>
              <div className="flex gap-3">
              <button
                onClick={() => setActiveTab('add')}
                className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center ${
                  activeTab === 'add'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                <Plus className="w-4 h-4 mr-2" />
                Thêm Domain
              </button>
              <button
                onClick={() => setActiveTab('manage')}
                className={`px-4 py-2 rounded-xl transition-all duration-300 flex items-center ${
                  activeTab === 'manage'
                    ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                    : 'bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 border border-white/20'
                }`}
              >
                <Settings className="w-4 h-4 mr-2" />
                Quản lý Domain
              </button>
            </div>
         </div>

                   {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
           <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-4 border border-blue-500/30 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-gray-300 text-sm">Tổng Domain</p>
                 <p className="text-2xl font-bold text-white">{stats.total}</p>
               </div>
               <Globe className="w-8 h-8 text-blue-400" />
             </div>
           </div>
           
           <div className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-4 border border-green-500/30 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-gray-300 text-sm">Hoạt động</p>
                 <p className="text-2xl font-bold text-green-400">{stats.active}</p>
               </div>
               <CheckCircle className="w-8 h-8 text-green-400" />
             </div>
           </div>
           
           <div className="bg-gradient-to-r from-yellow-500/20 to-orange-600/20 backdrop-blur-sm rounded-2xl p-4 border border-yellow-500/30 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-gray-300 text-sm">Đang xử lý</p>
                 <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
               </div>
               <Clock className="w-8 h-8 text-yellow-400" />
             </div>
           </div>
           
           <div className="bg-gradient-to-r from-red-500/20 to-pink-600/20 backdrop-blur-sm rounded-2xl p-4 border border-red-500/30 transform hover:-translate-y-2 hover:scale-105 transition-all duration-300">
             <div className="flex items-center justify-between">
               <div>
                 <p className="text-gray-300 text-sm">Lỗi</p>
                 <p className="text-2xl font-bold text-red-400">{stats.error}</p>
               </div>
               <XCircle className="w-8 h-8 text-red-400" />
             </div>
           </div>
         </div>
       </div>

                             </div>

               <div className="p-6 lg:p-8">
          {/* Tab Content */}
          {activeTab === 'add' && (
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                         {/* Form thêm domain */}
             <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-white/20 shadow-2xl transform hover:scale-[1.02] transition-all duration-500">
              <div className="flex items-center mb-6">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mr-4">
                  <Plus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Thêm Domain Mới</h2>
                  <p className="text-gray-300">Tạo website mới với domain của bạn</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Chọn mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Chọn chế độ xây dựng:
                  </label>
                                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <label className="relative">
                      <input
                        type="radio"
                        name="mode"
                        value="gmc"
                        checked={mode === 'gmc'}
                        onChange={(e) => setMode(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                        mode === 'gmc'
                          ? 'border-blue-500 bg-blue-500/20 text-white'
                          : 'border-white/20 bg-slate-700/80 text-gray-200 hover:border-white/40 hover:bg-slate-600/80'
                      }`}>
                        <Zap className="w-6 h-6 mb-2" />
                        <div className="font-medium">Tạo GMC</div>
                        <div className="text-xs opacity-75">Website mới</div>
                      </div>
                    </label>
                    
                    <label className="relative">
                      <input
                        type="radio"
                        name="mode"
                        value="run"
                        checked={mode === 'run'}
                        onChange={(e) => setMode(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                        mode === 'run'
                          ? 'border-purple-500 bg-purple-500/20 text-white'
                          : 'border-white/20 bg-slate-700/80 text-gray-200 hover:border-white/40 hover:bg-slate-600/80'
                      }`}>
                        <TrendingUp className="w-6 h-6 mb-2" />
                        <div className="font-medium">Chạy GMC</div>
                        <div className="text-xs opacity-75">Tối ưu hiệu suất</div>
                      </div>
                    </label>
                    
                    <label className="relative">
                      <input
                        type="radio"
                        name="mode"
                        value="sample"
                        checked={mode === 'sample'}
                        onChange={(e) => setMode(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                        mode === 'sample'
                          ? 'border-green-500 bg-green-500/20 text-white'
                          : 'border-white/20 bg-slate-700/80 text-gray-200 hover:border-white/40 hover:bg-slate-600/80'
                      }`}>
                        <Shield className="w-6 h-6 mb-2" />
                        <div className="font-medium">Site mẫu</div>
                        <div className="text-xs opacity-75">Template sẵn</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Modern Domain Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-3">
                    Danh sách Domain:
                  </label>
                  
                  {/* Domain Input Field */}
                  <div className="relative">
                    <div className="bg-slate-700/90 backdrop-blur-sm border border-white/30 rounded-2xl p-4 min-h-[120px] focus-within:border-blue-500/50 focus-within:ring-2 focus-within:ring-blue-500/20 transition-all duration-300">
                      {/* Domain Tags */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {domains.map((domain) => (
                          <div
                            key={domain.id}
                            className="flex items-center space-x-2 bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-sm border border-blue-500/30 rounded-xl px-3 py-2 text-white"
                          >
                            <Globe className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-medium">{domain.name}</span>
                            <button
                              type="button"
                              onClick={() => removeDomain(domain.id)}
                              className="text-blue-300 hover:text-red-400 transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      
                      {/* Input Field */}
                      <input
                        type="text"
                        value={domainInput}
                        onChange={(e) => handleInputChange(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Nhập domain và nhấn Enter (ví dụ: example.com)"
                        className="w-full bg-transparent border-none outline-none text-white placeholder-gray-400 text-sm"
                      />
                    </div>
                    
                    {/* Suggestions */}
                    {suggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-blue-500/50 rounded-2xl p-2 z-10 shadow-2xl">
                        {suggestions.map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => addDomain(suggestion)}
                            className="w-full text-left px-3 py-2 text-white hover:bg-blue-600/50 rounded-lg transition-colors text-sm"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Bulk Import */}
                  <div className="mt-4">
                    <details className="group">
                      <summary className="cursor-pointer text-sm text-gray-400 hover:text-white transition-colors flex items-center">
                        <Plus className="w-4 h-4 mr-2" />
                        Import nhiều domain
                      </summary>
                      <div className="mt-3 p-4 bg-slate-700/80 rounded-xl border border-white/20">
                        <textarea
                          placeholder="Nhập nhiều domain
example1.com, example2.com"
                          rows={4}
                          className="w-full bg-slate-600/80 border border-white/30 rounded-xl px-3 py-2 text-white placeholder-gray-300 text-sm resize-none"
                          onChange={(e) => handleBulkAdd(e.target.value)}
                        />
                        <p className="text-xs text-gray-400 mt-2">
                          Hỗ trợ: dấu phẩy (,), chấm phẩy (;), xuống dòng
                        </p>
                      </div>
                    </details>
                  </div>
                  
                  {/* Domain Count */}
                  <div className="flex items-center justify-between mt-3 text-sm">
                    <span className="text-gray-400">
                      {domains.length} domain đã chọn
                    </span>
                    {domains.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setDomains([])}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        Xóa tất cả
                      </button>
                    )}
                  </div>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={loading || domains.length === 0}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-4 px-6 rounded-2xl font-medium hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300 shadow-lg"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin inline" />
                      Đang xử lý...
                    </>
                  ) : (
                    <>
                      <Globe className="w-5 h-5 mr-2 inline" />
                      Tạo Website ({domains.length} domain)
                    </>
                  )}
                </button>

                <div className="text-center">
                  <div className="inline-flex items-center px-4 py-2 bg-white/10 rounded-full text-sm text-gray-300">
                    <Server className="w-4 h-4 mr-2" />
                    IP xử lý: <strong className="ml-1">164.92.64.42</strong>
                  </div>
                </div>
              </form>
            </div>

                         {/* Info Panel */}
             <div className="space-y-6">
               <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-white/20 shadow-2xl">
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl mr-4">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Hướng dẫn sử dụng</h3>
                    <p className="text-gray-300">Cách tạo website hiệu quả</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                      1
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Chọn chế độ phù hợp</h4>
                      <p className="text-gray-300 text-sm">Tạo GMC cho website mới, Chạy GMC để tối ưu, Site mẫu cho template có sẵn</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                      2
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Thêm domain</h4>
                      <p className="text-gray-300 text-sm">Nhập từng domain hoặc import hàng loạt. Hệ thống tự động kiểm tra trùng lặp</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5">
                      3
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Theo dõi tiến trình</h4>
                      <p className="text-gray-300 text-sm">Hệ thống sẽ kiểm tra domain mỗi 30 giây và cập nhật trạng thái real-time</p>
                    </div>
                  </div>
                </div>
              </div>

              {checking && (
                <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 backdrop-blur-lg rounded-3xl p-6 border border-blue-500/30 shadow-2xl">
                  <div className="flex items-center text-blue-300">
                    <RefreshCw className="w-6 h-6 mr-3 animate-spin" />
                    <div>
                      <div className="font-medium">Đang kiểm tra domain...</div>
                      <div className="text-sm opacity-75">Lần {checkAttempt}/{maxAttempts}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

                 {activeTab === 'manage' && (
           <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 border border-white/20 shadow-2xl">
            <div className="flex items-center mb-8">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl mr-4">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Quản lý Domain</h2>
                <p className="text-gray-300">Theo dõi và quản lý tất cả domain của bạn</p>
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto custom-scrollbar">
              {domainList.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Globe className="w-12 h-12 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-medium text-white mb-2">Chưa có domain nào</h3>
                  <p className="text-gray-400">Bắt đầu bằng cách thêm domain đầu tiên của bạn</p>
                </div>
              ) : (
                domainList.map((domain) => (
                  <div
                    key={domain.id}
                    className="bg-white/5 backdrop-blur-lg rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 transform hover:scale-[1.02] group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          {getStatusIcon(domain.status)}
                          <span className="font-semibold text-white text-lg">
                            {domain.domain_name}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            domain.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            domain.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>
                            {getStatusText(domain.status)}
                          </span>
                        </div>
                        
                                                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-400">Mode:</span>
                            <span className="text-white font-medium">{domain.mode}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-400">Tạo:</span>
                            <span className="text-white">
                              {new Date(domain.created_at).toLocaleDateString('vi-VN')}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-400">Cập nhật:</span>
                            <span className="text-white">
                              {new Date(domain.updated_at).toLocaleDateString('vi-VN')}
                            </span>
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={() => deleteDomain(domain.id)}
                        className="p-3 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100"
                        title="Xóa domain"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Custom Scrollbar Styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
};

export default DomainManagement; 